import { api } from '@/lib/api';
import type { StockLot, StockLotFilters } from './types';
import type { StockLotCreateValues, StockLotUpdateValues } from './schema';

/**
 * Stock-lots REST client over the shared axios instance.
 *
 * Endpoints (Phase 4c):
 *   GET    /stock-lots              list with optional filters
 *   GET    /stock-lots/:id          detail
 *   POST   /stock-lots              create (qty starts at 0)
 *   PATCH  /stock-lots/:id          update thresholds/notes only
 *   DELETE /stock-lots/:id          soft-delete
 *
 * The list endpoint accepts ?vesselId, ?partId, ?location,
 * ?lowStockOnly. We only wire the first three plus lowStockOnly here;
 * `includeDeleted` is admin-only territory and stays unwired in F7.3.
 */
export const stockLotsApi = {
  list: async (filters: StockLotFilters = {}): Promise<StockLot[]> => {
    const { data } = await api.get<StockLot[]>('/stock-lots', {
      params: filters,
    });
    return data;
  },

  getById: async (id: string): Promise<StockLot> => {
    const { data } = await api.get<StockLot>(`/stock-lots/${id}`);
    return data;
  },

  create: async (input: StockLotCreateValues): Promise<StockLot> => {
    const { data } = await api.post<StockLot>('/stock-lots', input);
    return data;
  },

  update: async (
    id: string,
    patch: StockLotUpdateValues,
  ): Promise<StockLot> => {
    const { data } = await api.patch<StockLot>(`/stock-lots/${id}`, patch);
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/stock-lots/${id}`);
  },
};
